import type React from "react";
import { useMemo } from "react";
import TableBody_team from "./TableBody_team";
import type { CellViolationInfo } from "@/services/validation";

import type * as type from "@vTypes";
import { useRosterDisplayShiftColumns } from "@/hooks/shift/useRosterDisplayShiftColumns";
import { useRosterSubmissionQuery } from "@hooks/roster-craete/useRosterSubmissionQuery";
import { useScheduleStore } from "@store/scheduleStore";
import { useIsAdmin } from "@hooks/useAuth.ts";
import { useGroupStore } from "@store/groupStore.ts";
import useRosterConfig from "@/hooks/useRosterConfig";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import { useShiftLookupMaps } from "@/hooks/shift/useShiftLookupMaps";
import { useTeamsQuery } from "@/hooks/roster-craete/useTeamsQuery";
import { filterTeam } from "@utils/rosterCreate-util.ts";
import { sortTeamGroupsByTeamName } from "@/utils/team-sort-util";
import type { PrevTailData } from "@/hooks/roster-craete/useRosterPrevTail";
import type {
  EmergencyBulkPeriodPick,
  EmergencyPostApplyHighlight,
  EmergencyReplacementSlotPick,
} from "@/types/emergency-replacement.types";

export default function Roster_create_Tab2({
  copyNurses,
  setCopyNurses,
  fullNurseSchema,
  setFullNurseSchema,
  availableShifts,
  isEditMode,
  selectedShift,
  modDayShift,
  saveNurseSequence,
  fullNurseSchemaAddPreceptor,
  prevTailData,
  showPerShiftStats = false,
  cellViolationMap,
  selectIdForSideprofile,
  scheduleYear = 0,
  scheduleMonth = 0,
  schedulePayloadYear,
  schedulePayloadMonth,
  currentGroupId = null,
  isEmergencyPickMode = false,
  onEmergencySlotPick,
  emergencySlotPick = null,
  isEmergencyBulkPickMode = false,
  onEmergencyBulkPick,
  emergencyBulkPeriodPick = null,
  emergencyBulkSelectedSteps = null,
  emergencyReplacementCandidateNurseId = null,
  emergencyPostApplyHighlight = null,
}: {
  copyNurses: any;
  setCopyNurses: (value: any) => void;
  fullNurseSchema: any[];
  setFullNurseSchema: React.Dispatch<React.SetStateAction<any[]>>;
  availableShifts: type.availableShifts[];
  isEditMode: boolean;
  selectedShift: type.Type_availableShifts | null;
  modDayShift: (
    nurseIndex: number,
    scheduleDay: number,
    changeShift: string,
    cellShiftDbId?: number | null
  ) => void;
  saveNurseSequence: () => void;
  fullNurseSchemaAddPreceptor: any[];
  prevTailData?: PrevTailData | null;
  showPerShiftStats?: boolean;
  /** 상위 `useRosterValidation` 결과 — 전체 보기와 동일 검증(최소인원 포함) */
  cellViolationMap: Map<string, CellViolationInfo>;
  selectIdForSideprofile?: (bool: boolean, nurseID: string) => void;
  /** `row_NurseRosterList` 파견·휴직 기간 바 — 전체 보기(TableBody)와 동일 */
  scheduleYear?: number;
  scheduleMonth?: number;
  schedulePayloadYear?: number;
  schedulePayloadMonth?: number;
  currentGroupId?: string | null;
  isEmergencyPickMode?: boolean;
  onEmergencySlotPick?: (pick: EmergencyReplacementSlotPick) => void;
  emergencySlotPick?: EmergencyReplacementSlotPick | null;
  isEmergencyBulkPickMode?: boolean;
  onEmergencyBulkPick?: (pick: {
    nurseId: string;
    nurseName: string;
    dayIndex: number;
    dateISO: string;
    shiftColor?: string;
  }) => void;
  emergencyBulkPeriodPick?: EmergencyBulkPeriodPick | null;
  emergencyBulkSelectedSteps?: Array<{ date: string; nurseId: string }> | null;
  emergencyReplacementCandidateNurseId?: string | null;
  emergencyPostApplyHighlight?: EmergencyPostApplyHighlight | null;
}) {
  const { curDate } = useScheduleStore();
  const isAdminHook = useIsAdmin();
  const { selectedGroupId: selectedGroupIdFromStore } = useGroupStore();

  // teamsSchema 가져오기
  const targetGroupId = isAdminHook ? (selectedGroupIdFromStore ?? null) : null;
  const { data: teamsSchema = [] } = useTeamsQuery(targetGroupId);

  // nurseListByTeam, nurseListNoTeam 내부에서 생성
  const [nurseListByTeam, nurseListNoTeam] = useMemo(() => {
    if (!copyNurses) {
      return [
        [] as type.NurseListByTeam[],
        {
          teams: { team_name: "미등록", team_id: null, team_members: [] },
          nurses: [],
        } as type.NurseListByTeam,
      ];
    }
    // 팀 스키마가 없으면 filterTeam과 같이 전원 미등록
    if (!teamsSchema || teamsSchema.length === 0) {
      return [
        [] as type.NurseListByTeam[],
        {
          teams: { team_name: "미등록", team_id: null, team_members: [] },
          nurses: copyNurses,
        } as type.NurseListByTeam,
      ];
    }
    const result = filterTeam(teamsSchema, copyNurses);
    return [
      sortTeamGroupsByTeamName(result[0] as type.NurseListByTeam[]),
      result[1] as type.NurseListByTeam,
    ];
  }, [teamsSchema, copyNurses]);

  // 제출된 간호사 목록을 직접 가져오기
  const { data: submissionData } = useRosterSubmissionQuery({
    year: curDate?.year,
    month: curDate?.month,
    groupId: isAdminHook ? (selectedGroupIdFromStore ?? null) : null,
  });
  const currentSubmittedList = submissionData?.submitted_nurses ?? [];

  // useRosterValidation에 필요한 데이터 가져오기
  const { data: serverConfig } = useRosterConfig();
  const { data: shiftsData } = useShiftsQuery(
    isAdminHook ? (selectedGroupIdFromStore ?? undefined) : undefined
  );

  const { lookupMaps: shiftLookupMaps } = useShiftLookupMaps(
    availableShifts,
    shiftsData ?? null
  );

  const {
    columnShiftIds: statsColumnShiftIds,
    displayShifts: statsDisplayShifts,
  } = useRosterDisplayShiftColumns(
    copyNurses ?? [],
    availableShifts,
    serverConfig?.use_mid,
    { includeBaselineMainTemplateColumns: true }
  );

  console.log("nurseListByTeam:", nurseListByTeam);

  return (
    <div className="tableBody_team_tablelist">
      {Array.isArray(nurseListByTeam) &&
        nurseListByTeam.map((teams: type.NurseListByTeam, idx: number) => (
          <TableBody_team
            key={idx}
            team={teams.teams}
            nurses={teams.nurses}
            copyNurses={copyNurses}
            setCopyNurses={setCopyNurses}
            fullNurseSchema={fullNurseSchema}
            setFullNurseSchema={setFullNurseSchema}
            fullNurseSchemaAddPreceptor={fullNurseSchemaAddPreceptor}
            availableShifts={availableShifts}
            shiftLookupMaps={shiftLookupMaps}
            currentSubmittedList={currentSubmittedList}
            isEditMode={isEditMode}
            selectedShift={selectedShift}
            modDayShift={modDayShift}
            saveNurseSequence={saveNurseSequence}
            cellViolationMap={
              cellViolationMap as unknown as Map<string, CellViolationInfo>
            }
            statsColumnShiftIds={statsColumnShiftIds}
            statsDisplayShifts={statsDisplayShifts}
            prevTailData={prevTailData}
            showPerShiftStats={showPerShiftStats}
            selectIdForSideprofile={selectIdForSideprofile}
            scheduleYear={scheduleYear}
            scheduleMonth={scheduleMonth}
            schedulePayloadYear={schedulePayloadYear}
            schedulePayloadMonth={schedulePayloadMonth}
            currentGroupId={currentGroupId}
            isEmergencyPickMode={isEmergencyPickMode}
            onEmergencySlotPick={onEmergencySlotPick}
            emergencySlotPick={emergencySlotPick}
            isEmergencyBulkPickMode={isEmergencyBulkPickMode}
            onEmergencyBulkPick={onEmergencyBulkPick}
            emergencyBulkPeriodPick={emergencyBulkPeriodPick}
            emergencyBulkSelectedSteps={emergencyBulkSelectedSteps}
            emergencyReplacementCandidateNurseId={
              emergencyReplacementCandidateNurseId
            }
            emergencyPostApplyHighlight={emergencyPostApplyHighlight}
          />
        ))}

      <TableBody_team
        team={nurseListNoTeam.teams}
        nurses={nurseListNoTeam.nurses}
        setCopyNurses={setCopyNurses}
        fullNurseSchema={fullNurseSchema}
        setFullNurseSchema={setFullNurseSchema}
        fullNurseSchemaAddPreceptor={fullNurseSchemaAddPreceptor}
        availableShifts={availableShifts}
        shiftLookupMaps={shiftLookupMaps}
        currentSubmittedList={currentSubmittedList}
        copyNurses={copyNurses}
        isEditMode={isEditMode}
        selectedShift={selectedShift}
        modDayShift={modDayShift}
        saveNurseSequence={saveNurseSequence}
        cellViolationMap={
          cellViolationMap as unknown as Map<string, CellViolationInfo>
        }
        statsColumnShiftIds={statsColumnShiftIds}
        statsDisplayShifts={statsDisplayShifts}
        prevTailData={prevTailData}
        showPerShiftStats={showPerShiftStats}
        selectIdForSideprofile={selectIdForSideprofile}
        scheduleYear={scheduleYear}
        scheduleMonth={scheduleMonth}
        schedulePayloadYear={schedulePayloadYear}
        schedulePayloadMonth={schedulePayloadMonth}
        currentGroupId={currentGroupId}
        isEmergencyPickMode={isEmergencyPickMode}
        onEmergencySlotPick={onEmergencySlotPick}
        emergencySlotPick={emergencySlotPick}
        isEmergencyBulkPickMode={isEmergencyBulkPickMode}
        onEmergencyBulkPick={onEmergencyBulkPick}
        emergencyBulkPeriodPick={emergencyBulkPeriodPick}
        emergencyBulkSelectedSteps={emergencyBulkSelectedSteps}
        emergencyReplacementCandidateNurseId={
          emergencyReplacementCandidateNurseId
        }
        emergencyPostApplyHighlight={emergencyPostApplyHighlight}
      />
    </div>
  );
}
